// socket-engine slot-layouts (DECISION-052) — composer slot-layouts that wrap the proven
// _socket-layout engine primitives (flat-pile / fan-spread / featured-hero), so multi-socket
// socket-driven fixtures (e.g. pile-table) place products through the ONE composer path.
//
// The composer's multi-socket loop passes ctx.socket (the catalogue socket, LOCAL TRS) +
// the fixture anchor; we compose the socket to WORLD, run the engine, and map its placements
// → the composer's slot shape ({position, quaternion}). Reuses the exact math Coord verified
// on the (now-retiring) socket render path, so the visual is unchanged. Comic-only for now.

import * as THREE from "../../../../vendor/three.module.js";
import { boxedComicReferenceMeters } from "../../../authoring/contracts.js";
import { registerSlotLayout } from "./index.js";
import { layoutProducts } from "../../_socket-layout.js";
import { composeSocketWorld } from "../../_socket-transform.js";

const COMIC_DIMS = { comic: { dimensions: {
	w: boxedComicReferenceMeters.width, h: boxedComicReferenceMeters.height, d: boxedComicReferenceMeters.thickness,
} } };

function quatForPlacement(pl) {
	if (pl.quaternion) return new THREE.Quaternion(pl.quaternion.x, pl.quaternion.y, pl.quaternion.z, pl.quaternion.w);
	const qFlat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), pl.rotationX || 0);
	const qYaw  = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), pl.rotationY || 0);
	return qYaw.multiply(qFlat);   // lay flat (local) then orient by world yaw
}

function makeEngineSlotLayout(algorithm) {
	return function computeViaEngine({ anchor, socket, productCount }) {
		if (!socket) return { slots: [], extras: { slotCount: 0, maxSlots: 0 } };
		// Catalogue socket is LOCAL to the fixture; compose with the anchor → world.
		const world = composeSocketWorld({ position: anchor.position, rotationY: anchor.rotationY }, socket);
		const socketFact = {
			socketId: socket.id, position: world.position, rotationY: world.rotationY,
			layout: algorithm, capacity: socket.capacity,
			accepts: socket.acceptedProductTypes || socket.accepts, params: socket.params,
		};
		const products = Array.from({ length: Math.max(0, productCount || 0) }, () => ({}));
		const plan = layoutProducts({ socket: socketFact, products, productDims: COMIC_DIMS });
		const slots = plan.placements.map((pl) => ({
			position: new THREE.Vector3(pl.x, pl.y, pl.z),
			quaternion: quatForPlacement(pl),
		}));
		const wc = new THREE.Vector3(world.position.x, world.position.y, world.position.z);
		return {
			slots,
			extras: {
				slotCount: slots.length, maxSlots: plan.capacity ?? slots.length,
				laneCenter: wc, slotStepVector: new THREE.Vector3(), slotQuaternion: new THREE.Quaternion(),
				heroExtract: new THREE.Vector3(wc.x, wc.y + 0.30, wc.z),
			},
		};
	};
}

registerSlotLayout("featured-hero", makeEngineSlotLayout("featured-hero"));
registerSlotLayout("fan-spread",    makeEngineSlotLayout("fan-spread"));
registerSlotLayout("flat-pile",     makeEngineSlotLayout("flat-pile"));

export { makeEngineSlotLayout };
